/**
 * @mentions in comments and group chats.
 *
 * The server decides who a comment or group message mentions and sends them back as
 * `mentions` ({ _id, username }); these helpers only find the @names in the text, so
 * each one the server recognised can render as a link. Same pattern as the server's: an @ right
 * after a letter or digit is an email address, not a mention. Some usernames are a whole
 * email address, so a name may carry an "@" of its own.
 * @see mintly-back/src/utils/mentions.js
 */
const MENTION = /(^|[^\w@])@([a-z0-9_][a-z0-9_.+@-]*)/gi;

/** A trailing "." (or "-", "+", "@") ends the sentence, not the name. */
const trimName = (name) => name.replace(/[.+@-]+$/, "");

/**
 * Split comment text into plain strings and `{ text, user }` mention parts. An @name
 * the server didn't recognise stays plain text.
 */
export function splitMentions(text, mentions = []) {
  const source = String(text || "");
  const byName = new Map(
    (mentions || [])
      .filter((m) => m && typeof m === "object" && m.username)
      .map((m) => [m.username.toLowerCase(), m])
  );
  if (!byName.size) return [source];

  const parts = [];
  let last = 0;
  for (const match of source.matchAll(MENTION)) {
    // The longest name that's someone: "@ana@mail.com" whole, else "@ana".
    const name = [trimName(match[2]), trimName(match[2].split("@")[0])].find(
      (n) => n && byName.has(n.toLowerCase())
    );
    if (!name) continue;
    const user = byName.get(name.toLowerCase());
    const at = match.index + match[1].length;
    if (at > last) parts.push(source.slice(last, at));
    parts.push({ text: `@${name}`, user });
    last = at + 1 + name.length;
  }
  if (last < source.length) parts.push(source.slice(last));
  return parts;
}

/**
 * The @name being typed just before the caret, if any: `{ query, start }`, where
 * `start` is the index of the "@". Null when the caret isn't in a mention.
 */
export function mentionAtCaret(text, caret) {
  const before = String(text || "").slice(0, caret);
  const match = /(^|[^\w@])@([a-z0-9_.+@-]{0,64})$/i.exec(before);
  if (!match) return null;
  return { query: match[2], start: caret - match[2].length - 1 };
}
